// Claude's proposal as changes to the settings (FR-31). The API has checked it when Claude answered (the fixed list,
// the catalog's limits and steps, at most 3 K from the machine's setpoint, never protection class S) and checks the set
// command once more when it is adopted (origin "ai"). Here only what the screen needs: the catalog still knows the
// value, it applies to the stop in use now, and it differs from the value now (old → new for the comparison).
import { findCommand, type CommandCatalog } from '@brewcore/protocol';
import type { ClaudeChange } from '@brewcore/app-contract';
import type { CommandInput } from '../core/commands.js';
import { t, type Lang } from '../core/i18n.js';
import type { StopMode } from '../core/recipe.js';
import { decimalsOf, formatNumber } from '../core/screens/format.js';
import type { ParamValue } from '../core/store.js';

export interface AiChange {
  id: string;
  from: ParamValue | null;
  to: ParamValue;
  /** grind only: which way it goes */
  how?: 'finer' | 'coarser';
}

export interface ChangeEnv {
  /** the values as the app shows them now */
  value: (id: string) => ParamValue | undefined;
  catalog: CommandCatalog;
  stopMode: StopMode;
}

/** The proposal against the values now: unknown, not applicable or unchanged values are left out. */
export function aiChanges(proposed: readonly ClaudeChange[] | undefined, env: ChangeEnv): AiChange[] {
  const out: AiChange[] = [];
  for (const c of proposed ?? []) {
    const cmd = findCommand(env.catalog, c.param);
    if (!cmd || cmd.kind !== 'set' || cmd.protection === 'S' || cmd.role !== 'member') continue;
    if (
      (c.param === 'brew.by_weight.target_weight' || c.param === 'brew.by_weight.lag') &&
      env.stopMode !== 'weight'
    )
      continue;
    if (c.param === 'brew.by_time.target_time' && env.stopMode !== 'time') continue;
    const now = env.value(c.param);
    const from = now === undefined ? null : now;
    const same =
      typeof c.to === 'number' && typeof now === 'number'
        ? Math.abs(c.to - now) < 1e-6
        : c.to === (now ?? null);
    if (same) continue;
    out.push({ id: c.param, from, to: c.to, ...(c.how ? { how: c.how } : {}) });
  }
  return out;
}

/** Adopting: every change in ONE set (checked and applied together, AD-11), marked as coming from Claude. */
export const changesCommand = (changes: readonly AiChange[]): CommandInput => ({
  kind: 'set',
  params: Object.fromEntries(changes.map((c) => [c.id, c.to])),
  origin: 'ai',
});

/**
 * "Rückgängig" after adopting: the values from before in ONE set (the user's own change, not Claude's); values the app
 * did not know before are left out. Null when nothing can be set back.
 */
export function undoCommand(changes: readonly AiChange[]): CommandInput | null {
  const back = changes.filter((c) => c.from !== null);
  if (!back.length) return null;
  return {
    kind: 'set',
    params: Object.fromEntries(back.map((c) => [c.id, c.from as ParamValue])),
    origin: 'ui',
  };
}

/** A value as the app shows it ("2,5", "93,0 °C", "an"). */
export function valueText(id: string, v: ParamValue | null, lang: Lang, catalog: CommandCatalog): string {
  const cmd = findCommand(catalog, id);
  const unit = cmd?.unit ? `\u00a0${cmd.unit}` : '';
  if (typeof v === 'boolean') return t(lang, v ? 'on' : 'off');
  if (typeof v === 'number') return `${formatNumber(v, decimalsOf(cmd?.step), lang)}${unit}`;
  return v || '–';
}

const how = (c: { how?: 'finer' | 'coarser' }, lang: Lang) =>
  c.how ? ` (${t(lang, c.how === 'finer' ? 'aiFiner' : 'aiCoarser')})` : '';

/** "Mahlgrad 2,5 → 2,4 (feiner)", "Dosis 18,0 g → 18,5 g", "Pre-Infusion aus → an" */
export function changeText(c: AiChange, lang: Lang, catalog: CommandCatalog): string {
  const label = findCommand(catalog, c.id)?.label[lang] ?? c.id;
  return `${label} ${valueText(c.id, c.from, lang, catalog)} → ${valueText(c.id, c.to, lang, catalog)}${how(c, lang)}`;
}

/** The name of a value as the catalog has it ("Mahlgrad"). */
export const changeLabel = (id: string, lang: Lang, catalog: CommandCatalog): string =>
  findCommand(catalog, id)?.label[lang] ?? id;

/** An adopted proposal: only the value it set ("Mahlgrad 2,4 (feiner)"). */
export function adoptedText(c: ClaudeChange, lang: Lang, catalog: CommandCatalog): string {
  const label = findCommand(catalog, c.param)?.label[lang] ?? c.param;
  return `${label} ${valueText(c.param, c.to, lang, catalog)}${how(c, lang)}`;
}
